import { GenericToolbar, type ToolbarFilterOption, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { applyClientFilters, applyClientSearch, applyClientSort, paginate } from './client-table';
import { buildToolbarDefinitions, columnsRevealedByFilters, quickFilterButtonLabel } from './descriptor';
import type { EmbeddedTableRenderContext, ResourceTableDescriptor } from './types';

interface EmbeddedResourceTableProps<TRow> {
    descriptor: ResourceTableDescriptor<TRow>;
    /** The whole set for this tab — filtering and pagination happen here, in JS. */
    rows: TRow[];

    /**
     * Per-usage column-visibility key, e.g. `leads:employee-show`. Distinct keys
     * let the same resource keep different column choices per page.
     */
    storageKey: string;

    /** Built by the resource's shared option builder, never derived in the page. */
    filterOptions: Record<string, ToolbarFilterOption[]>;
    columnFilterLoading?: Record<string, boolean>;

    hiddenColumnIds?: readonly string[];
    hiddenFilterIds?: readonly string[];

    defaultPerPage?: number;
    /** Omit to follow the viewport: grid on mobile, table on desktop. */
    defaultViewMode?: ToolbarViewMode;
    emptyMessage?: string;

    renderTable: (context: EmbeddedTableRenderContext<TRow>) => ReactNode;
    /** Omit to disable the grid view mode entirely. */
    renderCard?: (row: TRow) => ReactNode;

    /** Escape hatch for genuinely page-specific toolbar bits. */
    customFilters?: ReactNode;
    customFiltersPosition?: 'start' | 'end';
    inlineFilters?: ReactNode;
}

/**
 * A resource table embedded in a detail page, filtered client-side.
 *
 * Detail pages used to hand-roll this — ten pieces of state, four useMemo chains
 * and a manual pagination slice per tab, which is how the same list ended up
 * looking different on three pages. Here the columns, filters and sort entries
 * come from the resource's descriptor, the same one its index page reads.
 *
 * Deliberately client-side only. Index pages keep useIndexFilters, because they
 * also own Inertia navigation, URL state and the loading flag; a `mode` prop here
 * would mean two state machines in one component, which is the mistake
 * UniversalDataTable already made.
 */
export function EmbeddedResourceTable<TRow>({
    descriptor,
    rows,
    storageKey,
    filterOptions,
    columnFilterLoading,
    hiddenColumnIds,
    hiddenFilterIds,
    defaultPerPage = 10,
    defaultViewMode,
    emptyMessage,
    renderTable,
    renderCard,
    customFilters,
    customFiltersPosition,
    inlineFilters,
}: EmbeddedResourceTableProps<TRow>) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();

    const definitions = useMemo(
        () => buildToolbarDefinitions(descriptor, t, { hiddenColumnIds, hiddenFilterIds }),
        [descriptor, t, hiddenColumnIds, hiddenFilterIds],
    );

    const [viewMode, setViewMode] = useState<ToolbarViewMode>(defaultViewMode ?? (isMobile ? 'grid' : 'table'));
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 250);
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(defaultPerPage);

    const {
        visibleColumns,
        showColumn,
        hideColumn,
    } = useColumnVisibility(storageKey, definitions.defaultVisibleColumns, definitions.allColumnIds);

    // Without a card renderer there is nothing to switch to, so the toggle would
    // offer a view that renders an empty grid.
    const showViewToggle = renderCard !== undefined;

    useEffect(() => {
        if (!showViewToggle) {
            setViewMode('table');
            return;
        }
        if (defaultViewMode === undefined) {
            setViewMode(isMobile ? 'grid' : 'table');
        }
    }, [isMobile, defaultViewMode, showViewToggle]);

    const processed = useMemo(() => {
        const searched = applyClientSearch(rows, descriptor, debouncedSearch);
        const filtered = applyClientFilters(searched, descriptor, columnFilters);

        return applyClientSort(filtered, descriptor, sortColumn, sortDirection);
    }, [rows, descriptor, debouncedSearch, columnFilters, sortColumn, sortDirection]);

    // Clamping rather than resetting: narrowing a filter should not throw away the
    // user's place when the page they were on still exists.
    const pagination = useMemo(() => paginate(processed, page, perPage), [processed, page, perPage]);

    useEffect(() => {
        if (pagination.meta.current_page !== page) {
            setPage(pagination.meta.current_page);
        }
    }, [pagination.meta.current_page, page]);

    // A filter you can select but whose column is hidden looks broken, so the
    // column comes back into view for as long as the filter is on.
    const effectiveVisibleColumns = useMemo(() => {
        const revealed = columnsRevealedByFilters(descriptor, columnFilters).filter((id) => definitions.allColumnIds.includes(id));

        if (revealed.every((id) => visibleColumns.has(id))) {
            return visibleColumns;
        }

        return new Set([...visibleColumns, ...revealed]);
    }, [descriptor, columnFilters, visibleColumns, definitions.allColumnIds]);

    const handleFilterChange = (column: string, values: string[]) => {
        setPage(1);
        setColumnFilters((previous) => {
            const next = { ...previous };
            if (values.length === 0) {
                delete next[column];
            } else {
                next[column] = values;
            }
            return next;
        });
    };

    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] ?? [];
        handleFilterChange(column, current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
    };

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setPage(1);
        setSortColumn(column);
        setSortDirection(direction);
    };

    const tableProps = {
        visibleColumns: effectiveVisibleColumns,
        columnFilters,
        columnFilterOptions: filterOptions,
        onFilterChange: handleFilterChange,
        sortColumn,
        sortDirection,
        onSortChange: handleSortChange,
        onColumnHide: hideColumn,
        onColumnShow: showColumn,
    };

    return (
        <div className="space-y-4">
            <GenericToolbar
                quickFilterButtonLabel={quickFilterButtonLabel(descriptor, t, columnFilters, { hiddenColumnIds, hiddenFilterIds })}
                quickFilterDefinitions={definitions.quickFilterDefinitions}
                columnDefinitions={definitions.columnDefinitions}
                extraFilterDefinitions={definitions.extraFilterDefinitions}
                excludeFromMoreFilters={definitions.excludeFromMoreFilters}
                showMoreFilters={definitions.showMoreFilters}
                columnFilterOptions={filterOptions}
                columnFilterLoading={columnFilterLoading}
                columnFilters={columnFilters}
                columnFilterSearch={columnFilterSearch}
                setColumnFilterSearch={setColumnFilterSearch}
                toggleSingleFilter={toggleSingleFilter}
                handleFilterChange={handleFilterChange}
                clearFilters={() => {
                    setColumnFilters({});
                    setSearch('');
                    setPage(1);
                }}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                isSortOpen={isSortOpen}
                setIsSortOpen={setIsSortOpen}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                clearSort={() => {
                    setSortColumn(null);
                    setSortDirection(null);
                }}
                search={search}
                setSearch={(value) => {
                    setPage(1);
                    setSearch(value);
                }}
                perPage={perPage}
                onPerPageChange={(next) => {
                    setPerPage(next);
                    setPage(1);
                }}
                viewMode={viewMode}
                setViewMode={setViewMode}
                showViewToggle={showViewToggle}
                visibleColumns={visibleColumns}
                handleColumnShow={showColumn}
                handleColumnHide={hideColumn}
                filteredCount={pagination.meta.total}
                resourceName={descriptor.resourceName}
                resourceNamePlural={descriptor.resourceNamePlural}
                sortableColumns={definitions.sortableColumns}
                customFilters={customFilters}
                customFiltersPosition={customFiltersPosition}
                inlineFilters={inlineFilters}
            />

            {viewMode === 'grid' && renderCard ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {pagination.items.map((row) => (
                        <div key={descriptor.getRowId(row)}>{renderCard(row)}</div>
                    ))}
                </div>
            ) : (
                renderTable({ rows: pagination.items, tableProps })
            )}

            <PaginationControls
                pagination={pagination.meta}
                onPageChange={setPage}
                onPerPageChange={(next) => {
                    setPerPage(next);
                    setPage(1);
                }}
                className="mt-2"
            />

            {/* The table renders its own empty row; only the card grid needs this. */}
            {viewMode === 'grid' && renderCard && pagination.items.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">{emptyMessage ?? t('common.no_results', 'No results found.')}</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
